import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getDownPayment, recordDownPayment, updateDownPayment } from '@/api/accounts.api';
import { ApiError } from '@/api/errors';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { formatCurrencyEGP, formatDateOnly } from '@/lib/format';
import type { DownPaymentResponse, RecordDownPaymentRequest } from '@/types/shared';
import './DownPaymentCard.css';

interface DownPaymentCardProps {
  studentCode: string;
  /** Accounts write role — gates the record/edit affordances (read stays open to all read roles). */
  canEdit: boolean;
}

interface DraftState {
  amount: string;
  paidOn: string;
  paymentMethod: string;
  receiptNumber: string;
  notes: string;
}

const EMPTY_DRAFT: DraftState = {
  amount: '',
  paidOn: new Date().toISOString().slice(0, 10),
  paymentMethod: '',
  receiptNumber: '',
  notes: '',
};

function toDraft(dp: DownPaymentResponse): DraftState {
  return {
    amount: Number(dp.amount).toFixed(2),
    paidOn: dp.paidOn.slice(0, 10),
    paymentMethod: dp.paymentMethod ?? '',
    receiptNumber: dp.receiptNumber ?? '',
    notes: dp.notes ?? '',
  };
}

/**
 * Accounts detail "Down payment" card — the one-time-per-student tracked first payment (NOT
 * per-plan). Wired to `GET/POST/PATCH /accounts/{studentCode}/down-payment`. Three states:
 *   - loading / error
 *   - recorded  -> shows the amount/date/method/receipt (+ an Edit form for write roles)
 *   - none yet  -> neutral empty state (+ a Record form for write roles)
 * "First time only" is enforced server-side (409 `DOWN_PAYMENT_EXISTS`); the UI simply never shows
 * a second Record form once one exists.
 */
export function DownPaymentCard({ studentCode, canEdit }: DownPaymentCardProps) {
  const { t, i18n } = useTranslation();
  const [downPayment, setDownPayment] = useState<DownPaymentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const load = useCallback((code: string) => {
    setLoading(true);
    setError(null);
    getDownPayment(code)
      .then((dp) => setDownPayment(dp))
      .catch(() => setError(t('downPayment.loadError')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load(studentCode);
  }, [studentCode, load]);

  function openForm() {
    setDraft(downPayment ? toDraft(downPayment) : EMPTY_DRAFT);
    setFormOpen(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const amountNum = Number(draft.amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      showErrorToast(t('downPayment.invalidAmount'));
      return;
    }
    const payload: RecordDownPaymentRequest = {
      amount: amountNum,
      paidOn: draft.paidOn,
      paymentMethod: draft.paymentMethod.trim() || undefined,
      receiptNumber: draft.receiptNumber.trim() || undefined,
      notes: draft.notes.trim() || undefined,
    };
    setSaving(true);
    try {
      const saved = downPayment
        ? await updateDownPayment(studentCode, payload)
        : await recordDownPayment(studentCode, payload);
      setDownPayment(saved);
      setFormOpen(false);
      showSuccessToast(t(downPayment ? 'downPayment.updated' : 'downPayment.recorded'));
    } catch (err) {
      const msg =
        err instanceof ApiError && err.code === 'DOWN_PAYMENT_EXISTS'
          ? t('downPayment.alreadyExists')
          : t('downPayment.saveError');
      showErrorToast(msg);
    } finally {
      setSaving(false);
    }
  }

  const isRtl = i18n.language.startsWith('ar');

  return (
    <section className="fa-down-payment-card" aria-busy={loading}>
      <div className="fa-down-payment-card__head">
        <h3 className="fa-down-payment-card__title">{t('downPayment.title')}</h3>
        {canEdit && !formOpen && (
          <button type="button" className="fa-btn fa-btn--ghost fa-btn--sm" onClick={openForm}>
            {t(downPayment ? 'downPayment.edit' : 'downPayment.record')}
          </button>
        )}
      </div>

      {loading ? (
        <p className="fa-down-payment-card__muted">{t('common.loading')}</p>
      ) : error ? (
        <p className="fa-down-payment-card__error">{error}</p>
      ) : formOpen ? (
        <form className="fa-down-payment-card__form" onSubmit={handleSubmit} noValidate dir={isRtl ? 'rtl' : 'ltr'}>
          <label className="fa-field">
            <span className="fa-field__label">{t('downPayment.amount')}</span>
            <input
             
              type="number"
              min="0.01"
              step="0.01"
              value={draft.amount}
              onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
              required
            />
          </label>
          <label className="fa-field">
            <span className="fa-field__label">{t('downPayment.paidOn')}</span>
            <input
             
              type="date"
              value={draft.paidOn}
              onChange={(e) => setDraft((d) => ({ ...d, paidOn: e.target.value }))}
              required
            />
          </label>
          <label className="fa-field">
            <span className="fa-field__label">{t('downPayment.method')}</span>
            <input
             
              type="text"
              maxLength={50}
              value={draft.paymentMethod}
              onChange={(e) => setDraft((d) => ({ ...d, paymentMethod: e.target.value }))}
            />
          </label>
          <label className="fa-field">
            <span className="fa-field__label">{t('downPayment.receipt')}</span>
            <input
             
              type="text"
              maxLength={30}
              value={draft.receiptNumber}
              onChange={(e) => setDraft((d) => ({ ...d, receiptNumber: e.target.value }))}
            />
          </label>
          <label className="fa-field fa-down-payment-card__notes">
            <span className="fa-field__label">{t('downPayment.notes')}</span>
            <input
             
              type="text"
              maxLength={255}
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            />
          </label>
          <div className="fa-down-payment-card__actions">
            <button type="submit" className="fa-btn fa-btn--primary fa-btn--sm" disabled={saving}>
              {t('common.save')}
            </button>
            <button
              type="button"
              className="fa-btn fa-btn--ghost fa-btn--sm"
              onClick={() => setFormOpen(false)}
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      ) : downPayment ? (
        <dl className="fa-down-payment-card__details">
          <div>
            <dt>{t('downPayment.amount')}</dt>
            <dd className="fa-down-payment-card__amount">{formatCurrencyEGP(Number(downPayment.amount), i18n.language)}</dd>
          </div>
          <div>
            <dt>{t('downPayment.paidOn')}</dt>
            <dd>{formatDateOnly(downPayment.paidOn, i18n.language)}</dd>
          </div>
          {downPayment.paymentMethod && (
            <div>
              <dt>{t('downPayment.method')}</dt>
              <dd>{downPayment.paymentMethod}</dd>
            </div>
          )}
          {downPayment.receiptNumber && (
            <div>
              <dt>{t('downPayment.receipt')}</dt>
              <dd>{downPayment.receiptNumber}</dd>
            </div>
          )}
          {downPayment.notes && (
            <div className="fa-down-payment-card__notes">
              <dt>{t('downPayment.notes')}</dt>
              <dd>{downPayment.notes}</dd>
            </div>
          )}
        </dl>
      ) : (
        <p className="fa-down-payment-card__muted">{t('downPayment.none')}</p>
      )}
    </section>
  );
}
